import { history } from 'umi';
import { omit } from 'lodash-es';
import { getPageData, getPageDataNew } from '@intelligence/services/marketProfile';
import { getCountryFlag } from '@/pages/home/_config/countries';
import { isEmptyValue } from '@home/utils/common';

import type { IEntityType } from '@intelligence/pages/GenreProfile/typings';

interface IMarketProfileDetailsInsightsServiceBaseParams {
  entity_type: IEntityType;
  source: string;
  market: string;
  start_date: string;
  end_date: string;
  [key: string]: any;
}

interface IMobileNewReleaseBaseMetricsValues {
  app_top_free: number;
  app_top_grossing: number;
  google_top_free: number;
  google_top_grossing: number;
}

interface IMobileNewReleaseMetricsValues extends IMobileNewReleaseBaseMetricsValues {
  app_top_free_change: number;
  app_top_grossing_change: number;
  google_top_free_change: number;
  google_top_grossing_change: number;
}

const BASE_MOBILE_NEW_RELEASE_METRIC_MAPPING: Record<keyof IMobileNewReleaseBaseMetricsValues, string> = {
  app_top_free: 'appstore top free',
  app_top_grossing: 'appstore top grossing',
  google_top_free: 'googleplay top free',
  google_top_grossing: 'googleplay top grossing',
};

const EMPTY_BASE_MOBILE_NEW_RELEASE_METRICS: IMobileNewReleaseBaseMetricsValues = {
  app_top_free: 0,
  app_top_grossing: 0,
  google_top_free: 0,
  google_top_grossing: 0,
};

const EMPTY_MOBILE_NEW_RELEASE_METRICS: IMobileNewReleaseMetricsValues = {
  ...EMPTY_BASE_MOBILE_NEW_RELEASE_METRICS,
  app_top_free_change: 0,
  app_top_grossing_change: 0,
  google_top_free_change: 0,
  google_top_grossing_change: 0,
};

const analysisMobileNewReleaseMetricsData = (metrics?: any[]): IMobileNewReleaseMetricsValues => {
  if (!Array.isArray(metrics) || !metrics.length) return EMPTY_MOBILE_NEW_RELEASE_METRICS;
  return Object.keys(EMPTY_BASE_MOBILE_NEW_RELEASE_METRICS).reduce<any>((acc, cur) => {
    // @ts-ignore
    const key = BASE_MOBILE_NEW_RELEASE_METRIC_MAPPING[cur];
    // @ts-ignore
    const metric = metrics.find((metric) => metric?.source === key);
    const { value, value_ratio: valueRatio } = metric || {};
    return {
      ...acc,
      [cur]: isEmptyValue(value) ? 0 : value,
      [`${cur}_change`]: isEmptyValue(value) || isEmptyValue(valueRatio) ? 0 : valueRatio - value,
    };
  }, EMPTY_MOBILE_NEW_RELEASE_METRICS);
};

const mobileReleaseGameMapper = (game?: any, market?: string) => {
  const {
    unified_id: gameId,
    name: gameName,
    rank: gameRank,
    cover: gameCover,
    heat: gameHeat,
    publisher: gamePublisher,
    metric_list: metrics,
    release_time: releaseTime,
  } = game || {};

  const metricsValues = analysisMobileNewReleaseMetricsData(metrics);

  return {
    ...metricsValues,
    id: gameId || '',
    name: gameName || '',
    gameIcon: gameCover || '',
    heat: gameHeat || '',
    rank: gameRank,
    desc: gamePublisher || '',
    // @ts-ignore
    countryId: history.location.query.marketNameEn,
    conutryFlag: getCountryFlag(market || ''),
    release_time: releaseTime,
  };
};

const mobileReleaseGameComparator = (game1?: any, game2?: any): number => {
  // 解构对象并处理undefined情况
  const getValue = (game: any, key: string) => game?.[key] ?? -Infinity;
  // 判断是否rank指标都为空值
  const isAllMetricValueEmpty = (game: any) => keys.every((key) => !getValue(game, key as string));

  // 定义比较优先级顺序
  const keys: (keyof typeof game1)[] = ['app_top_free', 'app_top_grossing', 'google_top_free', 'google_top_grossing'];
  const isGame1AllMetricValueEmpty = isAllMetricValueEmpty(game1);
  const isGame2AllMetricValueEmpty = isAllMetricValueEmpty(game2);

  if (isGame1AllMetricValueEmpty && isGame2AllMetricValueEmpty) return 0;
  if (isGame1AllMetricValueEmpty) return 1;
  if (isGame2AllMetricValueEmpty) return -1;

  // 依次比较每个属性
  for (const key of keys) {
    const val1 = getValue(game1, key as string);
    const val2 = getValue(game2, key as string);

    if (!val1 && !val2) return 0;
    if (!val1) return 1;
    if (!val2) return -1;

    // 升序排列：val1 - val2 > 0 会使得大的值排在后面
    const diff = val1 - val2;
    if (diff !== 0) return diff;
  }

  // 所有属性都相等
  return 0;
};

export const formatMobileNewReleaseGames = (games: any[], market?: string): any[] => {
  if (!Array.isArray(games) || !games.length) return [];
  return games.map((game) => mobileReleaseGameMapper(game, market)).sort(mobileReleaseGameComparator);
};

class MarketProfileDetailsInsightsAdapter {
  static getPageCommonData(url: string, params?: IMarketProfileDetailsInsightsServiceBaseParams) {
    return getPageData(url, omit(params, ['source']));
  }

  static getPageTrendsData(url: string, params?: IMarketProfileDetailsInsightsServiceBaseParams) {
    const { entity_type: entityType } = params || {};
    if (entityType !== 'mobile') return MarketProfileDetailsInsightsAdapter.getPageCommonData(url, params);
    return getPageData(url, params);
  }

  static async getPageNewReleaseData(url: string, params?: IMarketProfileDetailsInsightsServiceBaseParams) {
    const { entity_type: entityType, market, start_date: startDate, end_date: endDate } = params || {};
    if (entityType !== 'mobile') return MarketProfileDetailsInsightsAdapter.getPageCommonData(url, params);
    const date = startDate || endDate || '';
    const mobileNewReleaseGamesRes = await getPageDataNew('mobileNewReleaseGames', {
      entity_type: entityType,
      market,
      date,
    });
    const { data, req } = mobileNewReleaseGamesRes || {};
    const { date: reqDate } = req || {};
    return {
      new_release_mobile_games: data || [],
      date: reqDate || '',
    };
  }

  static getPageData(url: string, params?: IMarketProfileDetailsInsightsServiceBaseParams) {
    switch (url) {
      case 'detail/insights_cr10/trend': {
        return MarketProfileDetailsInsightsAdapter.getPageTrendsData(url, params);
      }
      case 'detail/insights_new_release': {
        return MarketProfileDetailsInsightsAdapter.getPageNewReleaseData(url, params);
      }
      case 'detail/insights_bigriser2':
      case 'detail/insights_upcomming': {
        return MarketProfileDetailsInsightsAdapter.getPageCommonData(url, params);
      }
      default: {
        return getPageData(url, params);
      }
    }
  }
}

export default MarketProfileDetailsInsightsAdapter;
